<template>
  <div class="recipe my-0 py-1">
    <!-- Existing Recipe Row (clickable) -->
    <div v-if="!isNew" class="recipe-row" @click="viewRecipe">
      <div class="recipe-drag drag-handle" @click.stop>
        <b-icon icon="drag" />
      </div>
      <img class="recipe-image" :src="thumbnailSrc" alt="">
      <div class="recipe-info">
        <div class="recipe-title">{{ recipe.title }}</div>
        <div class="recipe-meta">
          <span v-if="shouldDisplayName()" class="recipe-variant">{{ recipe.name }}</span>
        </div>
      </div>
      <span :class="['recipe-status', recipe.status]">{{ recipe.status }}</span>
      <b-tooltip
        :label="recipe.is_free ? 'Free to non-members' : 'Member recipe'"
        type="is-dark"
        position="is-top">
        <div :class="['recipe-star', { inactive: !recipe.is_free }]">
          <b-icon icon="star" size="is-small" />
        </div>
      </b-tooltip>
      <div class="recipe-actions" @click.stop>
        <button class="recipe-action-btn delete" title="Remove from plan" @click="remove">
          <b-icon icon="delete" size="is-small" />
        </button>
      </div>
    </div>

    <!-- Add New Recipe Row -->
    <div v-else class="add-recipe-row">
      <b-icon icon="plus" class="add-icon" />
      <b-autocomplete
        class="recipe-autocomplete"
        v-model="recipe.title"
        field="title"
        :data="suggestions"
        :loading="isLoading"
        placeholder="Search recipes to add..."
        open-on-focus
        keep-first
        @typing="getAsyncData"
        @select="option => selected(option)">
        <template slot-scope="props">
          <div class="media p-2">
            <div class="media-left">
              <img width="48" :src="`${optionThumbnailSrc(props.option)}`">
            </div>
            <div class="media-content">
              <h1 v-if="props.option.is_variant" class="is-size-5 has-text-weight-bold pb-0 mb-0">
                {{ props.option.title }}
                <span class="has-text-success">({{ props.option.name }})</span>
              </h1>
              <h1 v-else class="is-size-5 has-text-weight-bold pb-0 mb-0">{{ props.option.title }}</h1>
              <p class="is-size-7 mt-1">
                <span :class="[
                  'tag',
                  {'is-danger is-light': props.option.status == 'draft'},
                  {'is-warning': props.option.status == 'test'},
                  {'is-info': props.option.status == 'fix'},
                  {'is-success': props.option.status == 'live'}
                ]">
                  <b>{{ props.option.status }}</b>
                </span>
                <b-tag class="mx-1" :type="props.option.is_free ? 'is-success' : 'is-white'">
                  <b-icon size="is-small" :type="props.option.is_free ? 'is-white' : 'is-default'" icon="star" />
                </b-tag>
                Serves: <b>{{ props.option.servings }}</b>,
                Ingredients: <b>{{ props.option.ingredients.length }}</b>,
                Prep: <b>{{ props.option.prepTime }}</b>,
                Cook: <b>{{ props.option.cookTime }}</b>
              </p>
            </div>
          </div>
        </template>
      </b-autocomplete>
    </div>
  </div>
</template>

<script>
//import db from "@/firebase/db"
import debounce from 'lodash/debounce'
import search from '../search'

export default {
  name: 'EditRecipe',
  props: {
    recipeIn: Object,
    isNew: Boolean
  },
  data() {
    return {
      recipe: null,
      query: '',
      suggestions: [],
      isLoading: false
    }
  },
  created() {
    this.recipe = this.recipeIn ?? { id: null, title: "", images: null, is_free: false };
    this.recipe.is_free = this.recipe.is_free ?? false;
    
  },
  mounted() {
    this.suggestions = this.getAsyncData("");
  },
  computed: {
    isValidRecipe() {
      return this.recipe.id != null
    },
    filteredSuggestions() {
      return this.suggestions.filter(option => {
        return option.title.toLowerCase().indexOf(this.recipe.title.toLowerCase()) >= 0
      });
    },
    thumbnailSrc() {
      if (this.recipe.images != null && this.recipe.images[0] != null) {
        return this.recipe.images[0].src;
      }
      return require("../assets/listing-placeholder.png");
    }
  },
  methods: {
      getAsyncData: debounce(function (query) {
        // String update
        if (this.query !== query) {
            this.query = query;
            this.suggestions = [];
        }
        // String cleared
        if (!query.length) {
            this.suggestions = []
            return
        }
        
        this.isLoading = true;

        let searchParameters = {
          'q'         : query,
          'preset'    : 'admin_recipe_search',
          'sort_by': '_text_match:desc',
          'per_page': '100'
        };

        // NB!!! 
        // This search is hardcoded to the live Typsense Cluster! 
        // TODO: install a local version for developing locally! 
        let that = this;
        search.collections('recipes')
          .documents()
          .search(searchParameters)
          .then(function (searchResults) {
            that.suggestions = searchResults.hits.map((hit) => hit.document );
          })
          .finally(() => {
            that.isLoading = false;
          })
      }, 500),
    selected(option) {
      if (option == null) {
        return;
      }

      this.recipe.id = option.id;
      this.recipe.title = option.title;
      this.recipe.status = option.status;
      this.recipe.is_free = option.is_free;
      
      if (option.published_at != null){
        this.recipe.published_at = option.published_at;
      }
      
      if (option.name != null){
        this.recipe.name = option.name;
      }
      this.recipe.images = option.images;
      if (option.cookTime != null){
        this.recipe.cookTime = option.cookTime;
      }
      if (option.prepTime != null){
        this.recipe.prepTime = option.prepTime;
      }
      
      this.recipe.servings = option.servings;
      this.recipe.ingredients_count = option.ingredients.length;

      // TODO: primary tags - when we support tags! 

      setTimeout(() => this.add(), 50); // timeout here is weird but allows us time to clear the empty input field
    },
    add() {
      this.$emit('add-recipe', this.recipe);
      this.recipe = { id: null, title: "", images: null, is_free: false };
    },
    remove() {
      this.$emit('remove-recipe', this.recipe);
    },
    viewRecipe() {
      this.$router.push({ name: 'RecipeView', params: { id: this.recipe.id } })
    },
    makeFree() {
      this.$emit('make-recipe-free', this.recipe);
    },
    shouldDisplayName() {
      return (this.recipe.name != null) && (this.recipe.name != "");
    },
    customOptionTitle(option) {
      let internalNameString = (option.name != null) && (option.name != "") ? ` (${option.name})`: "";
      return `${option.title}${internalNameString}`;
    },
    optionThumbnailSrc(option) {
      let image = option.images[0];
      if (image != null) {
        return image.src;
      }
      return null;
    }
  }
}
</script>

<style scoped>
.recipe-row {
  display: grid;
  grid-template-columns: 24px 48px 1fr auto auto auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  background: var(--bg-tertiary, #f1f3f9);
  border-radius: var(--radius-sm, 8px);
  border: 1px solid transparent;
  transition: all 0.15s;
  cursor: pointer;
}

.recipe-row:hover {
  background: var(--white, #fff);
  border-color: var(--border-light, #e2e8f0);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06));
}

.recipe-drag {
  color: var(--text-muted, #94a3b8);
  cursor: grab;
  opacity: 0.4;
  transition: opacity 0.15s;
}

.recipe-row:hover .recipe-drag {
  opacity: 1;
}

.recipe-image {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  background: var(--border-light, #e2e8f0);
}

.recipe-info {
  min-width: 0;
}

.recipe-title {
  font-weight: 500;
  font-size: 14px;
  color: var(--text-primary, #1e293b);
  line-height: 1.3;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recipe-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.recipe-variant {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: var(--feeds-purple, #a78bfa);
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}

.recipe-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.recipe-status.live {
  background: var(--feeds-green, #00857a);
  color: white;
}

.recipe-status.draft {
  background: #fef2f2;
  color: #dc2626;
}

.recipe-status.test {
  background: #fef3c7;
  color: #92400e;
}

.recipe-status.fix {
  background: #dbeafe;
  color: #1e40af;
}

.recipe-star {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--feeds-yellow, #ffc233);
  color: #92400e;
  border-radius: 6px;
  flex-shrink: 0;
}

.recipe-star.inactive {
  background: transparent;
  color: var(--border-medium, #cbd5e1);
}

.recipe-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}

.recipe-row:hover .recipe-actions {
  opacity: 1;
}

.recipe-action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-muted, #94a3b8);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s;
}

.recipe-action-btn:hover {
  background: var(--bg-tertiary, #f1f3f9);
  color: var(--text-secondary, #64748b);
}

.recipe-action-btn.delete:hover {
  background: #fef2f2;
  color: #dc2626;
}

/* Add Recipe Row */
.add-recipe-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: transparent;
  border: 2px dashed var(--border-medium, #cbd5e1);
  border-radius: var(--radius-md, 12px);
  transition: all 0.2s;
}

.add-recipe-row:focus-within {
  border-color: var(--feeds-green, #00857a);
  background: var(--feeds-green-light, #e6f5f3);
}

.add-icon {
  color: var(--text-muted, #94a3b8);
}

.recipe-autocomplete {
  flex: 1;
}

.recipe-autocomplete :deep(.control) {
  width: 100%;
}

.recipe-autocomplete :deep(input) {
  border: none;
  background: transparent;
  box-shadow: none;
}
</style>
